/**
 * Public advisor deposit page — no login. Institution / advisor opens the
 * magic link, sees who's asking + what they want, drops a PDF, done.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Upload, Loader2, CheckCircle2, AlertTriangle, FileText, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function InboxDepositPage() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [form, setForm] = useState({ depositor_name: "", depositor_email: "", depositor_institution: "", document_kind: "quote", file: null });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/inbox/public/${token}`)
      .then(r => setView(r.data))
      .catch(e => setError(e?.response?.data?.detail || "This link is invalid or has expired."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setUploadError("");
    if (!form.file) { setUploadError("Please choose a PDF or document."); return; }
    if (!form.depositor_name || !form.depositor_email) { setUploadError("Please tell us who you are."); return; }
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("depositor_name", form.depositor_name);
    fd.append("depositor_email", form.depositor_email);
    if (form.depositor_institution) fd.append("depositor_institution", form.depositor_institution);
    fd.append("document_kind", form.document_kind);
    setUploading(true);
    try {
      const { data } = await axios.post(`${API}/inbox/public/${token}/deposit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceipt(data);
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (error) {
    return <PublicShell><ErrorCard message={error} /></PublicShell>;
  }
  if (!view) {
    return <PublicShell><div className="flex items-center gap-2 text-sm text-slate-500 py-12 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Verifying link…</div></PublicShell>;
  }
  if (receipt) {
    return <PublicShell><ReceiptCard receipt={receipt} consumer_first_name={view.consumer_first_name} /></PublicShell>;
  }

  return (
    <PublicShell>
      <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="deposit-page">
        {/* Consumer context */}
        <div className="px-6 lg:px-10 py-6 text-white" style={{ background: NAVY }}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Altara Verified request
          </div>
          <h1 className="mt-2 font-display text-2xl lg:text-3xl font-semibold">
            {view.consumer_first_name} has requested a document
          </h1>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <ContextRow label="Product" value={(view.product_type || "").replace(/_/g, " ")} />
            {view.product_name_hint && <ContextRow label="Details" value={view.product_name_hint} />}
            {view.institution_hint && <ContextRow label="Asking" value={view.institution_hint} />}
            <ContextRow label="Link expires" value={view.expires_at ? new Date(view.expires_at).toLocaleDateString() : "—"} />
          </div>
          {view.note && (
            <div className="mt-4 rounded-xl bg-white/10 p-3 text-sm italic">&ldquo;{view.note}&rdquo;</div>
          )}
        </div>

        {/* Deposit form */}
        <form onSubmit={submit} className="p-6 lg:p-10 space-y-4">
          <div>
            <h2 className="text-sm font-semibold" style={{ color: NAVY }}>Drop the document here</h2>
            <p className="text-xs text-slate-500 mt-0.5">PDF, image or text · max 10 MB. It goes directly into {view.consumer_first_name}&apos;s wallet — never through email.</p>
          </div>

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) setForm({ ...form, file: f }); }}
            onClick={() => fileInputRef.current?.click()}
            data-testid="deposit-dropzone"
            className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition ${dragOver ? "border-slate-400 bg-slate-50" : form.file ? "border-emerald-300 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
          >
            {form.file ? (
              <div className="flex items-center justify-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-emerald-600" />
                <span className="font-medium" style={{ color: NAVY }}>{form.file.name}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); setForm({ ...form, file: null }); }} className="text-slate-400 hover:text-rose-600 ml-2"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 mx-auto text-slate-400" />
                <div className="mt-3 text-sm text-slate-600">Drop file here or <span className="underline" style={{ color: NAVY }}>choose a file</span></div>
              </>
            )}
            <input ref={fileInputRef} type="file" data-testid="deposit-file" className="hidden" onChange={e => setForm({ ...form, file: e.target.files?.[0] || null })} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <FormField label="Your name">
              <input value={form.depositor_name} onChange={e => setForm({ ...form, depositor_name: e.target.value })} data-testid="depositor-name" required className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Your email">
              <input type="email" value={form.depositor_email} onChange={e => setForm({ ...form, depositor_email: e.target.value })} data-testid="depositor-email" required className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Institution (optional)">
              <input value={form.depositor_institution} onChange={e => setForm({ ...form, depositor_institution: e.target.value })} data-testid="depositor-institution" placeholder={view.institution_hint || "e.g. Sanlam"} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            </FormField>
            <FormField label="Document kind">
              <select value={form.document_kind} onChange={e => setForm({ ...form, document_kind: e.target.value })} data-testid="doc-kind" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <option value="quote">Quote / proposal</option>
                <option value="disclosure">Disclosure / TCF</option>
                <option value="policy_wording">Policy wording</option>
                <option value="supporting">Supporting document</option>
              </select>
            </FormField>
          </div>

          {uploadError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 p-3 flex gap-2 items-start">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}

          <button type="submit" disabled={uploading || !form.file} data-testid="deposit-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition disabled:opacity-40" style={{ background: NAVY, color: "white" }}>
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Deposit to {view.consumer_first_name}&apos;s wallet</>}
          </button>

          <div className="text-[10px] text-slate-500 leading-relaxed pt-1">
            By submitting you confirm you have the right to send this document to {view.consumer_first_name}. This deposit is logged with a SHA-256 hash and timestamp — Altara-verified evidence of delivery.
          </div>
        </form>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen py-8 lg:py-16 px-4" style={{ background: "#F8F9FA" }}>
      <div className="max-w-2xl mx-auto">
        <a href="/" className="flex items-center gap-2 mb-6">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-display font-bold text-white" style={{ background: NAVY }}>A</div>
          <div className="leading-tight">
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Altara</div>
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Verified consumer wallet</div>
          </div>
        </a>
        {children}
        <div className="mt-6 text-[10px] text-slate-400 text-center">
          Altara Consumer Trust Infrastructure · <a href="/" className="underline">altara.co.za</a>
        </div>
      </div>
    </div>
  );
}

function ContextRow({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/60">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ReceiptCard({ receipt, consumer_first_name }) {
  const verifyUrl = receipt.verify_url;
  const certId = receipt.certificate_id;
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
  return (
    <div className="rounded-3xl bg-white shadow-xl border border-slate-100 overflow-hidden" data-testid="deposit-receipt">
      <div className="px-6 lg:px-10 py-8 text-white flex items-center gap-4" style={{ background: NAVY }}>
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
          <CheckCircle2 className="w-6 h-6" style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Delivered · Altara-verified</div>
          <h1 className="font-display text-2xl font-semibold mt-1">Thanks — it&apos;s in {consumer_first_name}&apos;s wallet</h1>
        </div>
      </div>
      <div className="p-6 lg:p-10">
        <p className="text-sm text-slate-700">Your document has been securely delivered to {consumer_first_name}. NAVI is now reading it and preparing a plain-language explanation for them.</p>

        {certId && (
          <div className="mt-6 rounded-2xl border p-4" style={{ background: `${GOLD}10`, borderColor: `${GOLD}55` }} data-testid="receipt-certificate">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: GOLD }}>
                <FileText className="w-4 h-4" style={{ color: NAVY }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: NAVY }}>Altara Trust Certificate</div>
                <div className="font-display text-lg font-semibold break-all mt-0.5" style={{ color: NAVY }}>{certId}</div>
                <p className="text-[12px] text-slate-700 mt-1">Ed25519-signed proof of delivery + integrity. Save the PDF for your compliance file — anyone can independently verify it with the link below.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {verifyUrl && (
                    <a href={verifyUrl} target="_blank" rel="noreferrer" data-testid="receipt-verify-link" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium border" style={{ background: NAVY, color: "white", borderColor: NAVY }}>
                      <ShieldCheck className="w-3 h-3" /> Open verify page
                    </a>
                  )}
                  <a href={`${API_BASE}/trust/certificates/${certId}/pdf`} target="_blank" rel="noreferrer" data-testid="receipt-pdf" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium border border-slate-200 bg-white hover:bg-slate-50" style={{ color: NAVY }}>
                    <FileText className="w-3 h-3" /> Download PDF
                  </a>
                  <a href={`${API_BASE}/trust/certificates/${certId}`} target="_blank" rel="noreferrer" data-testid="receipt-json" className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-medium border border-slate-200 bg-white hover:bg-slate-50" style={{ color: NAVY }}>
                    <FileText className="w-3 h-3" /> Signed JSON
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-slate-50 border border-slate-200 p-4 space-y-2 text-[12px] font-mono text-slate-700">
          <div><span className="text-slate-500">Delivery timestamp · </span>{new Date(receipt.delivered_at).toLocaleString()}</div>
          <div><span className="text-slate-500">Document ID · </span>{receipt.document_id}</div>
          <div className="break-all"><span className="text-slate-500">SHA-256 · </span>{receipt.sha256}</div>
          {receipt.envelope_sha256 && <div className="break-all"><span className="text-slate-500">Certificate hash · </span>{receipt.envelope_sha256}</div>}
          <div><span className="text-slate-500">Uploads on this request · </span>{receipt.upload_count} of {receipt.max_uploads}</div>
        </div>

        <div className="mt-6 rounded-xl p-4" style={{ background: `${GOLD}15`, border: `1px solid ${GOLD}55` }}>
          <div className="text-xs text-slate-700 leading-relaxed">
            <strong>For your compliance records:</strong> This receipt + the linked Trust Certificate are Altara-verified evidence of TCF-aligned disclosure — timestamped, hashed, cryptographically signed, and delivered directly to the consumer.
          </div>
        </div>

        {receipt.upload_count < receipt.max_uploads && (
          <button onClick={() => window.location.reload()} data-testid="upload-another" className="mt-6 text-sm font-medium underline" style={{ color: NAVY }}>
            Need to upload a revised version? →
          </button>
        )}
      </div>
    </div>
  );
}

function ErrorCard({ message }) {
  return (
    <div className="rounded-3xl bg-white shadow-xl border border-rose-200 overflow-hidden" data-testid="deposit-error">
      <div className="px-6 lg:px-10 py-8 flex items-center gap-4 bg-rose-50">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-rose-600">
          <AlertTriangle className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-800">Link problem</div>
          <h1 className="font-display text-xl font-semibold text-rose-900 mt-1">{message}</h1>
        </div>
      </div>
      <div className="p-6 lg:p-10">
        <p className="text-sm text-slate-700">Please ask the consumer to send you a fresh request link. Altara links expire after 7 days for safety.</p>
      </div>
    </div>
  );
}
